import { Telemetry, ValidTelemetryEvent } from '~commonInfra/services/Telemetry';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

/**
 * PFT (Product Feature Telemetry) event fired when a user saves a new or
 * edited description on an External Asset Details (EAD) catalog asset --
 * database, table, virtual connection, or virtual connection table. Datasource
 * and workbook descriptions are intentionally excluded; they're Tableau-owned
 * content, not external assets.
 *
 * Mapped in Juno to:
 *   Tableau Cloud > Catalog > External Asset Details (Cloud) > Update Description
 *
 * Wire event name: `description_updated`
 * Action verb:     `completed` (added downstream by Juno-registered schema)
 * Org ID type:     `tenant_id`
 *
 * Emitted after the description-set REST call resolves successfully (i.e.,
 * the resolved IResult carries no errors). Matches catalog-ui's flipped
 * post-review behavior so both halves of the shared Juno schema mean
 * "successful save", not "attempted save".
 *
 * `description_source` discriminates between the legacy manual editor (this
 * file) and the Einstein-generative dialog (instrumented in catalog-ui side at
 * `DescriptionRepository.tsx`). The catalog-ui column inline editor also emits
 * with `description_source: 'manual'` -- it's manual entry, just a different
 * surface.
 *
 * PII discipline: no raw description text. We log only length-in-characters
 * (bucketing left to the dashboard) and presence-booleans.
 */
export const DESCRIPTION_UPDATED_EVENT_TYPE = 'description_updated';

export type DescriptionSource = 'manual' | 'generative';

/** Content types in scope for EAD description tracking. */
const EAD_DESCRIBABLE_CONTENT_TYPES: ReadonlySet<string> = new Set([
  'database',
  'table',
  'virtualConnection',
  'virtualConnectionTable',
]);

export interface DescriptionUpdatedTelemetryEvent extends ValidTelemetryEvent {
  /** Wire event name. Always `description_updated`. */
  type: typeof DESCRIPTION_UPDATED_EVENT_TYPE;
  /**
   * PFT (Product Feature Telemetry) Standard Attribute for tenant routing. On Tableau Cloud
   * the tenant is the site, so this equals `site_luid`. Set per-event on the EAD telemetry
   * helpers rather than on `Telemetry.getCommonProperties()` so non-EAD vpc events (owned by
   * other teams) don't inherit a Standard Attribute their schemas don't declare.
   */
  tenant_id: string;
  /** Catalog content type. */
  content_type: string;
  /**
   * Connector backend, or empty string when unavailable.
   *
   * Populated for database / table via `DatabaseAndTableMapper`, and for
   * virtualConnection / virtualConnectionTable via the PublishedConnection mappers once the
   * upstream REST `connectionType` field is populated by the server-side change
   * (W-22931576 / monolith follow-up). Until that ships, VC/VCT emits fall back to empty
   * string here -- consistent with the `?? ''` sentinel at the emit call site.
   */
  connection_type: string;
  /** `'manual'` for the legacy editor and column inline editor; `'generative'` for the Einstein-suggested dialog. */
  description_source: DescriptionSource;
  /** True when the asset already had a non-empty description before this save. */
  had_description_before: boolean;
  /** True when the new description is non-empty. False indicates the user blanked out an existing description. */
  has_description_after: boolean;
  /** Character count of the prior description (0 when absent). */
  description_length_before: number;
  /** Character count of the new description. */
  description_length_after: number;
}

/**
 * True when the content type is an external asset whose description-updates
 * we want to instrument for the EAD PFT feature. Datasource and workbook
 * are explicitly out of scope.
 */
export function isEadDescribableContentType(contentType: string): boolean {
  return EAD_DESCRIBABLE_CONTENT_TYPES.has(contentType);
}

/**
 * Emit `description_updated` for an EAD asset's description save. The
 * `.completed` action verb is registered on the Juno schema and applied
 * by Endeavour downstream -- the wire `type` field is the bare event name.
 *
 * Caller is responsible for first checking `isEadDescribableContentType` --
 * we don't filter here so the helper stays a pure shape-builder.
 */
export function sendDescriptionUpdatedTelemetry(params: {
  readonly contentType: string;
  readonly item: IContentInfo;
  readonly previousDescription: string;
  readonly newDescription: string;
  readonly source: DescriptionSource;
}): void {
  const { contentType, item, previousDescription, newDescription, source } = params;

  Telemetry.sendEvent<DescriptionUpdatedTelemetryEvent>({
    type: DESCRIPTION_UPDATED_EVENT_TYPE,
    tenant_id: getSessionManagementServiceInstance().sessionSiteLuid(),
    content_type: contentType,
    connection_type: item.catalogConnectionType ?? '',
    description_source: source,
    had_description_before: previousDescription.length > 0,
    has_description_after: newDescription.length > 0,
    description_length_before: previousDescription.length,
    description_length_after: newDescription.length,
  });
}
